import { type ToastActionElement, type ToastProps } from "@/components/ui/toast";

type ToastFunction = (props: { 
  title?: string; 
  description: string; 
  action?: ToastActionElement; 
  variant?: "default" | "destructive"; 
}) => void;

export interface DataPoint {
  rpm: number;
  torque: number;
  horsepower: number;
  gear: string;
}

interface CsvParserOptions {
  measurement: 'metric' | 'imperial';
  toast: ToastFunction;
  minAccelPercent?: number; // Optional, defaults to 75%
}

export interface ParsedData {
  data: DataPoint[];
  maxRow: DataPoint | null;
  logType: 'MHD' | 'BM3' | 'UNKNOWN';
  rpmRange: {
    min: number;
    max: number;
  };
  peakHorsepower: number;
}

// Header pattern mappings for different log types
const headerMappings = {
  rpm: [
    /rpm/i,                     // MHD: "RPM (rpm)"
    /engine\s*speed.*1\/min/i   // BM3: "Engine speed[1/min]"
  ],
  torque: [
    /torque.*act\.?\s*clutch.*nm/i,         // MHD: "Torque act. clutch (Nm)"
    /\(RAM\).*torque.*clutch.*actual.*nm/i  // BM3: "(RAM) Torque at Clutch (Actual)[Nm]"
  ],
  gear: [
    /gear.*[-\]]/i  // Matches both "Gear (-)" and "Gear[-]"
  ],
  accelerator: [
    /Accel.*Ped.*Pos.*%/i,  // MHD: "Accel Ped. Pos. (%)"
    /Accel.*Pedal.*%/i      // BM3: "Accel. Pedal[%]"
  ]
};

// Helper function to find header index using multiple patterns
const findHeaderIndex = (headers: string[], patterns: RegExp[]) => {
  for (const pattern of patterns) {
    const index = headers.findIndex(header => pattern.test(header));
    if (index !== -1) return index;
  }
  return -1;
};

// Detect log type from headers
const detectLogType = (headers: string[]): 'MHD' | 'BM3' | 'UNKNOWN' => {
  const lastHeader = headers[headers.length - 1] || '';
  if (lastHeader.toLowerCase().includes('mhd')) return 'MHD';
  if (lastHeader.toLowerCase().includes('bootmod3')) return 'BM3';
  return 'UNKNOWN';
};

// Helper functions for data analysis
const calculateRpmRange = (data: DataPoint[]) => ({
  min: Math.min(...data.map(p => p.rpm)),
  max: Math.max(...data.map(p => p.rpm))
});

const calculatePeakHorsepower = (data: DataPoint[]) => 
  Math.max(...data.map(point => point.horsepower));

// Convert data point to imperial units if needed
const convertToImperial = (point: DataPoint): DataPoint => ({
  rpm: point.rpm,
  // Convert torque from Nm to lb-ft (1 Nm ≈ 0.737562 lb-ft)
  torque: point.torque * 0.737562,
  // Imperial horsepower: (torque (lb-ft) * rpm) / 5252
  horsepower: (point.torque * 0.737562 * point.rpm) / 5252,
  gear: point.gear
});

export const parseDynoData = async (file: File, options: CsvParserOptions): Promise<ParsedData> => {
  const text = await file.text();
  const rows = text.split("\n");
  let headerLineIndex = 0;

  // Skip comment lines
  if (rows.length > 0 && rows[0].startsWith("#")) {
    while (headerLineIndex < rows.length && rows[headerLineIndex].startsWith("#")) {
      headerLineIndex++;
    }
  }

  const headers = rows[headerLineIndex].split(",").map(h => h.trim());
  const logType = detectLogType(headers);
  
  // Find required column indices
  const rpmIndex = findHeaderIndex(headers, headerMappings.rpm);
  const torqueIndex = findHeaderIndex(headers, headerMappings.torque);
  const gearIndex = findHeaderIndex(headers, headerMappings.gear);

  // Find accelerator pedal column
  const accelIndex = findHeaderIndex(headers, headerMappings.accelerator);
  const minAccelPercent = options.minAccelPercent ?? 75;

  if (rpmIndex === -1 || torqueIndex === -1) {
    const formatSpecificHelp = logType === 'BM3' 
      ? '\nFor BM3: "Engine speed[1/min]", "(RAM) Torque at Clutch (Actual)[Nm]", and "Accel. Pedal[%]"'
      : logType === 'MHD'
      ? '\nFor MHD: "RPM (rpm)", "Torque act. clutch (Nm)", and "Accel Ped. Pos. (%)"'
      : '';

    options.toast({
      title: "Error",
      description: `Unable to find required data columns in ${logType} format.${formatSpecificHelp}`,
      variant: "destructive"
    });
    throw new Error("Missing required headers");
  }

  if (accelIndex === -1) {
    options.toast({
      title: "Warning",
      description: "Accelerator pedal data not found, showing all data points",
    });
  }

  const parsedData: DataPoint[] = [];
  
  // Parse data rows
  for (let i = headerLineIndex + 1; i < rows.length; i++) {
    const columns = rows[i].split(",");
    if (columns.length > Math.max(rpmIndex, torqueIndex)) {
      // Check accelerator pedal threshold
      const accelPercent = accelIndex !== -1 ? parseFloat(columns[accelIndex]) : 100;
      if (accelPercent >= minAccelPercent) {
        const rpm = parseFloat(columns[rpmIndex].trim());
        const torque = parseFloat(columns[torqueIndex].replace(/−/g, "-").trim());
        const gear = gearIndex !== -1 ? columns[gearIndex].trim() : "";
        if (!isNaN(rpm) && !isNaN(torque) && torque > 0) {
          // Calculate horsepower using the metric formula: HP = (TQ_Nm × 0.737562 × RPM) / 5252
          const horsepower = (torque * 0.737562 * rpm) / 5252;
          parsedData.push({ rpm, torque, horsepower, gear });
        }
      }
    }
  }

  if (parsedData.length === 0) {
    options.toast({
      title: "Error",
      description: "No valid data found in the file",
      variant: "destructive"
    });
    throw new Error("No valid data found");
  }

  // Find the maximum torque row and its gear
  const maxTorqueRow = parsedData.reduce((prev, curr) => curr.torque > prev.torque ? curr : prev, parsedData[0]);
  const gearToFilter = maxTorqueRow.gear.toLowerCase();

  // Get the contiguous block of data for the selected gear
  const firstIndex = parsedData.findIndex(point => point.gear.toLowerCase() === gearToFilter);
  const lastIndex = parsedData.length - 1 - parsedData.slice().reverse().findIndex(point => point.gear.toLowerCase() === gearToFilter);
  let filteredData = parsedData.slice(firstIndex, lastIndex + 1);

  // Deduplicate data points by RPM, keeping highest torque values
  const rpmMap = new Map<number, DataPoint>();
  filteredData.forEach(point => {
    const existingPoint = rpmMap.get(point.rpm);
    if (!existingPoint || point.torque > existingPoint.torque) {
      const torque = Math.max(point.torque, existingPoint?.torque || 0);
      rpmMap.set(point.rpm, {
        rpm: point.rpm,
        torque: torque,
        horsepower: (torque * 0.737562 * point.rpm) / 5252,
        gear: point.gear
      });
    }
  });
  
  // Convert map back to array and sort by RPM
  filteredData = Array.from(rpmMap.values()).sort((a, b) => a.rpm - b.rpm);

  // Convert to imperial if needed
  const finalData = options.measurement === 'imperial' 
    ? filteredData.map(convertToImperial)
    : filteredData;

  // Recompute max row after filtering and unit conversion
  const finalMaxRow = finalData.reduce((prev, curr) => curr.torque > prev.torque ? curr : prev, finalData[0]);

  options.toast({
    title: "Success",
    description: "Data loaded successfully"
  });

  return {
    data: finalData,
    maxRow: finalMaxRow,
    logType,
    rpmRange: calculateRpmRange(finalData),
    peakHorsepower: calculatePeakHorsepower(finalData)
  };
};
